import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

/**
 * Which branch the user is working in.
 *
 * A branch is a lens over the whole product, not a section of it — switch into Western
 * Sydney and the roster, participants, staff and reports are Western Sydney's. The choice
 * travels as an X-Branch-Id header on every request, so no page has to remember to pass
 * it and no page can forget.
 *
 * "All branches" is the default and is always available. A provider must be able to see
 * their whole organisation without having to know this feature exists.
 */
const BranchContext = createContext({
  branches: [],
  branchId: '',
  branch: null,
  setBranchId: () => {},
  multiBranch: false,
});

const KEY = 'rostery.activeBranch';

export function BranchProvider({ children }) {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchIdState] = useState(() => {
    try { return localStorage.getItem(KEY) || ''; } catch { return ''; }
  });

  // Keep the axios header in step with the choice, including on first render — otherwise
  // the first page load after a refresh would quietly be unscoped.
  useEffect(() => {
    if (branchId) api.defaults.headers.common['X-Branch-Id'] = branchId;
    else delete api.defaults.headers.common['X-Branch-Id'];
  }, [branchId]);

  useEffect(() => {
    let alive = true;
    // Only ask once somebody is signed in. This provider wraps the whole app including
    // the login page, and an unauthenticated call there is answered with a 401 that sends
    // the browser back to /login — remounting this provider and starting again.
    let token = null;
    try { token = localStorage.getItem('token'); } catch { /* private mode */ }
    if (!token) { setBranches([]); return undefined; }

    api.get('/locations')
      .then(({ data }) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data.filter(l => l.is_active !== false) : [];
        setBranches(list);
        // A remembered branch that has since been deleted, or that this user may no longer
        // use, must not leave them looking at an empty product with no explanation.
        setBranchIdState(cur => (cur && !list.some(l => l.id === cur) ? '' : cur));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [user?.id]);

  const setBranchId = useCallback((id) => {
    setBranchIdState(id || '');
    try {
      if (id) localStorage.setItem(KEY, id);
      else localStorage.removeItem(KEY);
    } catch { /* private mode: the choice just does not persist */ }
    if (id) api.defaults.headers.common['X-Branch-Id'] = id;
    else delete api.defaults.headers.common['X-Branch-Id'];
    // Every open list was loaded under the old lens, so reload rather than leave a mix of
    // branches on screen.
    window.location.reload();
  }, []);

  const branch = branches.find(b => b.id === branchId) || null;

  return (
    <BranchContext.Provider
      value={{ branches, branchId, branch, setBranchId, multiBranch: branches.length > 1 }}>
      {children}
    </BranchContext.Provider>
  );
}

export const useBranch = () => useContext(BranchContext);
export default BranchContext;
